"use client";

import { useRouter } from "next/navigation";
import { Pause, Play } from "lucide-react";
import { usePlayerStore, type PlayerTrack } from "@gerama/stores";
import { Button } from "@gerama/ui";
import { cn } from "@gerama/utils";

interface PlayButtonProps {
  track: PlayerTrack;
  queue?: PlayerTrack[];
  contextLabel: string;
  contextHref?: string;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
  /** `hero`: the big brand-coloured circle on detail headers; `ghost`: an icon-only row control. */
  appearance?: "default" | "hero" | "ghost";
}

/** The one control every song/album/playlist row uses to start playback — pushes into the global player store, never plays anything itself. */
export function PlayButton({
  track,
  queue,
  contextLabel,
  contextHref,
  className,
  size = "icon",
  appearance = "default",
}: PlayButtonProps) {
  const isCurrent = usePlayerStore(
    (s) => s.current()?.id === track.id && s.current()?.kind === track.kind,
  );
  const isPlaying = usePlayerStore((s) => s.isPlaying);
  const { playQueue, playSingle, togglePlay } = usePlayerStore.getState();
  const router = useRouter();

  function handleClick() {
    // Videos never go through the audio bar — their page has the video player.
    if (track.kind === "video") {
      router.push(`/videos/${track.code || track.id}`);
      return;
    }
    if (isCurrent) {
      togglePlay();
      return;
    }
    const audioQueue = queue?.filter((t) => t.kind !== "video");
    if (audioQueue && audioQueue.length > 1) {
      const startIndex = audioQueue.findIndex((t) => t.id === track.id && t.kind === track.kind);
      playQueue(audioQueue, Math.max(startIndex, 0), { label: contextLabel, href: contextHref });
    } else {
      playSingle(track, { label: contextLabel, href: contextHref });
    }
  }

  const playingNow = isCurrent && isPlaying;

  if (appearance === "hero") {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label={playingNow ? "توقف پخش" : "پخش"}
        className={cn(
          "solid-primary text-primary-foreground flex size-14 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95",
          className,
        )}
      >
        {playingNow ? (
          <Pause className="size-6 fill-current" />
        ) : (
          <Play className="size-6 fill-current" />
        )}
      </button>
    );
  }

  if (appearance === "ghost") {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label={playingNow ? "توقف پخش" : "پخش"}
        className={cn("text-foreground flex size-8 items-center justify-center", className)}
      >
        {playingNow ? (
          <Pause className="size-4 fill-current" />
        ) : (
          <Play className="size-4 fill-current" />
        )}
      </button>
    );
  }

  return (
    <Button
      type="button"
      size={size}
      className={cn("rounded-full", className)}
      onClick={handleClick}
      aria-label={playingNow ? "توقف پخش" : "پخش"}
    >
      {playingNow ? <Pause className="size-4" /> : <Play className="size-4" />}
    </Button>
  );
}
